import React, {useEffect, useRef, useState, useCallback} from "react";
import {Box, Stack, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import InstalledBaseTreeColumn from "../InstalledBaseTreeColumn";
import InstalledBaseEmptyIllustration from "./InstalledBaseEmptyIllustration";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useApolloClient} from "@apollo/client";
import {validateSelectedPath} from "../../utils/validateSelectedPath";
import {
    buildInstalledBasePathname,
    buildInstalledBaseSearch,
    parseDetailTab,
    parseLegacySelected,
    parseSelectedFromSplat,
    DetailTab,
} from "../../utils/installedBaseUrl";
import {isRiskReportsEnabled} from "../../utils/riskReportsEnabled";
import {isSlaEnabled} from "../../utils/slaEnabled";
import InstalledBaseNodeDetailPanel from "../InstalledBaseNodeDetailPanel";

interface Props {
}

// Stale deep links to the feature-flagged riskReports / networkScan tabs land
// on the Information tab while the respective flag is off.
const sanitizeDetailTab = (tab: DetailTab | null): DetailTab | null =>
    (tab === 'riskReports' && !isRiskReportsEnabled()) || (tab === 'networkScan' && !isSlaEnabled())
        ? 'information'
        : tab;

const LOCAL_STORAGE_KEY = 'installedBase_selectedNodes';
const SEARCH_STORAGE_KEY = 'installedBase_search';
const FILTER_STORAGE_KEY = 'installedBase_filter';

// Root column has parentId === null; map it to a stable string key.
const keyFor = (id: string | null) => id ?? 'root';

// Search/Filter auf die aktuell sichtbaren Spalten beschränken, damit
// URL und Local Storage nicht unbegrenzt wachsen, während man browst.
const pruneToPath = <T,>(map: Record<string, T>, selected: (string | null)[]): Record<string, T> => {
    const pathKeys = new Set(selected.map(keyFor));
    return Object.fromEntries(Object.entries(map).filter(([k]) => pathKeys.has(k)));
};

const InstalledBaseTree = (_props: Props) => {
    const {t} = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const splat = useParams()['*'];
    const apolloClient = useApolloClient();

    // Aktuelle Location für Effekte bereitstellen, ohne sie in deren
    // Dependencies aufnehmen zu müssen (vermeidet stale Closures).
    const locationRef = useRef(location);
    locationRef.current = location;

    // ID Pfad wird synchron zwischen URL und Local Storage gespeichert. Initial hat URL Prio und Local Storage ist fallback
    const getArrayFromURL = (): (string | null)[] | null => {
        // Alte Links mit JSON-kodiertem ?selected=-Param weiterhin unterstützen;
        // die URL wird durch den Write-Effekt aufs neue Format normalisiert.
        const legacy = parseLegacySelected(location.search);
        if (legacy) return legacy;
        const fromSplat = parseSelectedFromSplat(splat);
        return fromSplat.length > 1 ? fromSplat : null;
    };
    const getArrayFromLocalStorage = (): (string | null)[] => {
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (!stored) return [null];
            return JSON.parse(stored);
        } catch {
            return [null];
        }
    };

    // Search/Filter werden – wie der Pfad – pro Spalte (keyFor(parentId))
    // zwischen URL und Local Storage gespeichert. URL hat Prio, Local Storage ist Fallback.
    const getMapFromURL = <T,>(paramName: string): Record<string, T> | null => {
        try {
            const arrayString = new URLSearchParams(location.search).get(paramName);
            if (!arrayString) return null;
            const parsed = JSON.parse(arrayString);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    };
    const getMapFromLocalStorage = <T,>(storageKey: string): Record<string, T> => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored) return {};
            const parsed = JSON.parse(stored);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    };

    const cutPathAt = (index: number) => {
        // hält alles bis inklusive parent an Position index
        setSelectedNodeIds(prev => prev.slice(0, index + 1));
    }

    const initialSelected = getArrayFromURL() || getArrayFromLocalStorage();

    const [selectedNodeIds, setSelectedNodeIds] = useState<(string | null)[]>(initialSelected);
    const [searchByParent, setSearchByParent] = useState<Record<string, string>>(
        () => getMapFromURL<string>('search') ?? getMapFromLocalStorage<string>(SEARCH_STORAGE_KEY)
    );
    const [filterByParent, setFilterByParent] = useState<Record<string, Record<string, any>>>(
        () => getMapFromURL<Record<string, any>>('filter') ?? getMapFromLocalStorage<Record<string, any>>(FILTER_STORAGE_KEY)
    );
    // Detail panel for the deepest selected node. URL-only (no local storage):
    // ?detail=<tab> — the panel is transient, deep links restore it explicitly.
    const [detailTab, setDetailTab] = useState<DetailTab | null>(() => sanitizeDetailTab(parseDetailTab(location.search)));

    // Spalten-Index, dessen Liste nach einer Tastatur-Auswahl in der
    // Elternspalte das erste Item fokussieren soll.
    const [focusColumnIndex, setFocusColumnIndex] = useState<number | null>(null);

    const [skipRoot, setSkipRoot] = useState(false);
    const [rootCheckDone, setRootCheckDone] = useState(false);
    const [rootEmpty, setRootEmpty] = useState(false);

    const handleAutoSkipRoot = useCallback((nodeId: string) => {
        setSkipRoot(true);
        setRootCheckDone(true);

        setSelectedNodeIds(prev => {
            // Root-Index finden
            const rootIndex = prev.indexOf(null);
            // Wenn schon "null" und danach genau nodeId steht, nichts ändern
            if (prev[rootIndex + 1] === nodeId) return prev;
            // Wenn Pfad bereits tiefer geht, nichts abschneiden
            if (prev.length > rootIndex + 2) return prev;
            // Standard: Root + 1. Ebene
            return [null, nodeId];
        });
    }, []);

    const handleRootLoaded = useCallback(() => {
        if (!skipRoot) setRootCheckDone(true);
    }, [skipRoot]);

    useEffect(() => {
        // Validate the path from URL/localStorage against the backend on mount.
        // Renders optimistically; if the deepest node was moved or deleted,
        // the state is silently corrected here. See utils/validateSelectedPath.
        let cancelled = false;
        const snapshot = initialSelected;
        (async () => {
            const validated = await validateSelectedPath(snapshot, apolloClient);
            if (cancelled) return;
            setSelectedNodeIds(prev => {
                // If the user has already interacted (state diverged from snapshot),
                // do not overwrite their choice.
                if (
                    prev.length !== snapshot.length ||
                    !prev.every((v, i) => v === snapshot[i])
                ) {
                    return prev;
                }
                if (
                    validated.length === prev.length &&
                    validated.every((v, i) => v === prev[i])
                ) {
                    return prev;
                }
                return validated;
            });
        })();
        return () => {
            cancelled = true;
        };
        // kein automatisches setSkipRoot hier!
        // wir warten auf die Rückmeldung aus onAutoSkipRoot oder onRootLoaded
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Das Panel gehört immer zum tiefsten selektierten Knoten — ohne Auswahl
    // (nur Root-Ebene) gibt es keinen Zielknoten, der Param wird verworfen.
    const effectiveDetailTab = selectedNodeIds !== null && selectedNodeIds.length > 1 ? detailTab : null;

    const firstWriteDoneRef = useRef(false);
    useEffect(() => {
        if (selectedNodeIds === null) return;

        const prunedSearch = pruneToPath(searchByParent, selectedNodeIds);
        const prunedFilter = pruneToPath(filterByParent, selectedNodeIds);

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(selectedNodeIds));
        localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(prunedSearch));
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(prunedFilter));

        const pathname = buildInstalledBasePathname(selectedNodeIds);
        const search = buildInstalledBaseSearch(locationRef.current.search, prunedSearch, prunedFilter, effectiveDetailTab);
        // Nur navigieren, wenn sich die URL tatsächlich ändert — verhindert
        // doppelte History-Einträge und terminiert die Reset-/Sync-Schleife.
        // Der erste Write nach dem Mount ersetzt die URL statt zu pushen:
        // normalisiert Legacy-?selected=-Links und den Local-Storage-Restore,
        // ohne die History zu verschmutzen.
        if (locationRef.current.pathname !== pathname || (locationRef.current.search || '') !== search) {
            navigate({pathname, search}, {replace: !firstWriteDoneRef.current});
        }
        firstWriteDoneRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodeIds, searchByParent, filterByParent, effectiveDetailTab]);

    // Reset-Erkennung & URL→State-Sync: Klick auf "Installed Base" im Header
    // navigiert zu /installedBase ohne Segmente und ohne Query — das ist das
    // Reset-Signal (die Komponente bleibt gemountet, path="*" matcht beides).
    // Back/Forward oder externe Navigation mit Segmenten/Query synchronisiert
    // stattdessen den State aus der URL. Ein frischer Mount (Reload / Wechsel
    // von anderer Route) wird über didMountRef ausgenommen, damit dort
    // weiterhin aus dem Local Storage wiederhergestellt wird.
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        const legacy = parseLegacySelected(location.search);
        if (legacy) {
            // Alter ?selected=-Link, während die Komponente gemountet ist:
            // State übernehmen und die URL sofort aufs neue Format umleiten.
            setSelectedNodeIds(legacy);
            setSearchByParent(getMapFromURL<string>('search') ?? {});
            setFilterByParent(getMapFromURL<Record<string, any>>('filter') ?? {});
            setDetailTab(sanitizeDetailTab(parseDetailTab(location.search)));
            navigate({
                pathname: buildInstalledBasePathname(legacy),
                search: buildInstalledBaseSearch(
                    location.search,
                    pruneToPath(getMapFromURL<string>('search') ?? {}, legacy),
                    pruneToPath(getMapFromURL<Record<string, any>>('filter') ?? {}, legacy),
                    legacy.length > 1 ? sanitizeDetailTab(parseDetailTab(location.search)) : null,
                ),
            }, {replace: true});
            return;
        }

        // Eigene Navigation (Write-Effekt): URL entspricht bereits dem State.
        const expectedPathname = buildInstalledBasePathname(selectedNodeIds);
        const expectedSearch = buildInstalledBaseSearch(
            location.search,
            pruneToPath(searchByParent, selectedNodeIds),
            pruneToPath(filterByParent, selectedNodeIds),
            effectiveDetailTab,
        );
        if (location.pathname === expectedPathname && (location.search || '') === expectedSearch) {
            return;
        }

        const fromUrl = parseSelectedFromSplat(splat);
        if (fromUrl.length > 1 || location.search) {
            // Back/Forward oder externe Navigation: State aus der URL übernehmen.
            setSelectedNodeIds(fromUrl);
            setSearchByParent(getMapFromURL<string>('search') ?? {});
            setFilterByParent(getMapFromURL<Record<string, any>>('filter') ?? {});
            setDetailTab(sanitizeDetailTab(parseDetailTab(location.search)));
        } else {
            setSelectedNodeIds([null]);
            setSearchByParent({});
            setFilterByParent({});
            setDetailTab(null);
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            localStorage.removeItem(SEARCH_STORAGE_KEY);
            localStorage.removeItem(FILTER_STORAGE_KEY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    const prevLength = useRef<number | null>(null);
    const prevDetailOpen = useRef<boolean>(false);
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        // Warten bis der Root-Check fertig ist — vorher sind die tieferen
        // Spalten noch nicht im DOM, scrollWidth wäre falsch.
        if (!rootCheckDone) return;

        const detailOpen = effectiveDetailTab !== null;

        // Initial scroll (wenn prevLength noch null ist)
        if (prevLength.current === null) {
            requestAnimationFrame(() => {
                el.scrollTo({
                    left: el.scrollWidth,
                    behavior: 'auto', // direkt springen beim Initialisieren
                });
            });
        }
        // Scroll bei wachsender Anzahl von Spalten — oder wenn das
        // Detail-Panel gerade geöffnet wurde (es ersetzt die letzte Spalte,
        // die Spaltenanzahl wächst dabei nicht zwingend).
        else if (selectedNodeIds.length > prevLength.current || (detailOpen && !prevDetailOpen.current)) {
            requestAnimationFrame(() => {
                el.scrollTo({
                    left: el.scrollWidth,
                    behavior: 'smooth', // weich animieren
                });
            });
        }

        prevLength.current = selectedNodeIds.length;
        prevDetailOpen.current = detailOpen;
    }, [selectedNodeIds, rootCheckDone, effectiveDetailTab]);

    function nodeChangeHandler(id: string, index: number, options?: { viaKeyboard?: boolean }) {
        // Zeilenklick bleibt reine Baum-Navigation: ein offenes Detail-Panel
        // schließt sich und die normale Kinder-Spalte kehrt zurück.
        // Klick auf die Zeile des Panel-Knotens selbst: nur das Panel schließen —
        // der Knoten bleibt selektiert und seine Kinder-Spalte erscheint an der
        // Stelle des Panels (kein Deselect-Toggle).
        if (detailTab !== null && id === selectedNodeIds[selectedNodeIds.length - 1]) {
            setDetailTab(null);
            setFocusColumnIndex(null);
            return;
        }
        setDetailTab(null);
        if (id === selectedNodeIds[selectedNodeIds.length - 1]) {
            setFocusColumnIndex(null);
            handleNodeDeselect(index);
        } else {
            setFocusColumnIndex(options?.viaKeyboard ? index + 1 : null);
            handleNodeDeselect(index);
            setSelectedNodeIds(prev => {
                if (id === selectedNodeIds[prev.length]) {
                    return prev;
                }
                return [...prev, id]
            });

        }
    }

    // Öffnet das Detail-Panel für einen Knoten: der Knoten wird selektiert
    // (Pfad bis zu seiner Spalte + Knoten) und das Panel nimmt den Platz
    // seiner Kinder-Spalte ein. Ein bereits aktiver Tab bleibt erhalten.
    // Der Button ist ein Toggle: erneutes Drücken auf dem geöffneten Knoten
    // schließt das Panel wieder.
    function openNodeDetail(nodeId: string, index: number) {
        setFocusColumnIndex(null);
        if (detailTab !== null && nodeId === selectedNodeIds[selectedNodeIds.length - 1]) {
            setDetailTab(null);
            return;
        }
        setSelectedNodeIds(prev => [...prev.slice(0, index + 1), nodeId]);
        setDetailTab(prev => prev ?? 'information');
    }

    // Edit/Details aus Zeilen- und Header-Menü (IBM-1087): öffnet das Panel
    // immer auf Information, ohne Toggle. Liegt der Knoten schon im Pfad
    // (Header-Knoten oder selektierte Zeile), wird der Pfad dort gekappt,
    // sonst wird er wie bei openNodeDetail an seine Spalte angehängt.
    function editNodeDetail(nodeId: string, index: number) {
        setFocusColumnIndex(null);
        setSelectedNodeIds(prev => {
            const at = prev.indexOf(nodeId);
            return at !== -1 ? prev.slice(0, at + 1) : [...prev.slice(0, index + 1), nodeId];
        });
        setDetailTab('information');
    }

    function handleNodeDeselect(index: number): void {
        setSelectedNodeIds(prev => {
            if (prev.length === 0) {
                return prev;
            }
            return prev.slice(0, index + 1);
        });
    }

    // Follow a moved node to its new location: re-open the columns along the
    // target parent's path with the moved node selected. Any selection segments
    // below the moved node are preserved — that subtree moved along with it.
    const handleNodeMoved = useCallback((info: { movedNodeId: string; targetParentPathIds: string[] }) => {
        setSelectedNodeIds(prev => {
            const movedIndex = prev.indexOf(info.movedNodeId);
            const deeper = movedIndex >= 0 ? prev.slice(movedIndex + 1) : [];
            return [null, ...info.targetParentPathIds, info.movedNodeId, ...deeper];
        });
    }, []);

    if (selectedNodeIds === null) return null;

    // No accessible companies (root list empty & unfiltered): render the
    // no-access message centered across the whole content area instead of
    // an empty column. See list.tsx → onRootEmpty.
    if (rootEmpty) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    p: 3,
                }}
            >
                <InstalledBaseEmptyIllustration variant="locked" width={240} />
                <Typography
                    level="body-md"
                    sx={{pt: "20px", textAlign: "center", maxWidth: 380, color: "text.secondary"}}
                >
                    {t("You do not have access to any company in the Installed Base. Please contact your administrator to request access.")}
                </Typography>
            </Box>
        );
    }

    const detailNodeId = effectiveDetailTab !== null
        ? selectedNodeIds[selectedNodeIds.length - 1] ?? null
        : null;
    const detailOpen = detailNodeId !== null;

    return (
        <div ref={scrollContainerRef} style={{overflowX: 'auto', width: '100%', height: "100%"}}>
            <Stack sx={{flexDirection: 'row', height: "100%"}}>
                {selectedNodeIds.map((id, index) => {
                    if (skipRoot && index === 0) return null;
                    // Deeper columns wait for the root check so all visible
                    // columns appear together — avoids the layout-shift flicker
                    // where deeper columns render first and then get pushed
                    // right when the root column becomes visible.
                    if (index > 0 && !rootCheckDone) return null;
                    // The detail panel takes the slot of the deepest node's
                    // children column while it is open.
                    if (detailOpen && index === selectedNodeIds.length - 1) return null;

                    return <React.Fragment key={index}>
                        <InstalledBaseTreeColumn
                            key={index}
                            columnIndex={index}
                            parentId={id}
                            onChangeNode={(id, options) => nodeChangeHandler(id, index, options)}
                            focusFirstItemTrigger={focusColumnIndex === index}
                            onFirstItemFocused={() => setFocusColumnIndex(null)}
                            searchQuery={searchByParent[keyFor(id)] ?? ""}
                            onSearchChange={(q) => setSearchByParent(prev => ({...prev, [keyFor(id)]: q}))}
                            filter={filterByParent[keyFor(id)] ?? {}}
                            onFilterChange={(f) => setFilterByParent(prev => ({...prev, [keyFor(id)]: f}))}
                            selectedNodeId={selectedNodeIds[index+1]}
                            allSelectedNodeIds={selectedNodeIds}
                            deleteNodeCallback={(deletedNodeId) => {
                                // Cut the path right before the deleted node so its
                                // parent stays selected; deleting a node outside the
                                // selected path leaves the selection untouched.
                                if (deletedNodeId === detailNodeId) {
                                    setDetailTab(null);
                                }
                                setSelectedNodeIds(prev => {
                                    const idx = prev.indexOf(deletedNodeId);
                                    return idx === -1 ? prev : prev.slice(0, idx);
                                });
                            }}
                            moveNodeCallback={handleNodeMoved}
                            createNodeCallback={(newId) => {
                                // Creating selects the new node — like a row click
                                // this closes an open detail panel.
                                setDetailTab(null);
                                setSelectedNodeIds(prev => {
                                    const newList = [...prev.slice(0, index + 1), newId];
                                    return newList;
                                });
                            }}
                            onOpenNodeDetail={(nodeId) => openNodeDetail(nodeId, index)}
                            onEditNodeDetail={(nodeId) => editNodeDetail(nodeId, index)}
                            detailNodeId={detailNodeId}
                            onNoChildren={(colIndex) => cutPathAt(colIndex)}
                            autoSkipRoot={handleAutoSkipRoot}
                            onRootLoaded={index === 0 ? handleRootLoaded : undefined}
                            onRootEmpty={index === 0 ? setRootEmpty : undefined}
                            sx={index === 0 && !rootCheckDone ? { display: "none" } : {}}
                            visible={!(index === 0 && !rootCheckDone)}
                        />
                    </React.Fragment>
                })}

                {detailOpen && detailNodeId && rootCheckDone && effectiveDetailTab && (
                    <InstalledBaseNodeDetailPanel
                        key={detailNodeId}
                        nodeId={detailNodeId}
                        activeTab={effectiveDetailTab}
                        onTabChange={(tab) => setDetailTab(tab)}
                        onClose={() => setDetailTab(null)}
                        isRoot={selectedNodeIds.indexOf(detailNodeId) === 1}
                        deleteNodeCallback={(deletedNodeId) => {
                            setDetailTab(null);
                            setSelectedNodeIds(prev => {
                                const idx = prev.indexOf(deletedNodeId);
                                return idx === -1 ? prev : prev.slice(0, idx);
                            });
                        }}
                        moveNodeCallback={handleNodeMoved}
                    />
                )}
            </Stack>
        </div>
    );
}
export default InstalledBaseTree;
